/* /assets — company assets (custody): list, search/filter, add asset. */
import "./pay.css";
import { useMemo, useState } from "react";
import { useAssets, useCreateAsset, useToday } from "../../data/hooks";
import { daysTo } from "../../domain/dates";
import type { AssetInput } from "../../data/types";
import { useI18n } from "../../i18n";
import { gDate } from "../../lib/format";
import { Affix, Button, EmptyState, ErrorBox, ExpiryChip, Field, Input, Kpi, Modal, Money, PageHeader, SearchBox, Select, SelectField, Skeleton, TextField, errMsg, useToast } from "../../ui";
import { StatusChip, numOf, useEmpMap } from "./components/common";
import { curL } from "../../lib/country";

export const ASSET_TYPES: [string, string][] = [["Laptop", "حاسب محمول"], ["Phone", "جوال"], ["Vehicle", "مركبة"], ["SIM", "شريحة"], ["Access card", "بطاقة دخول"], ["Other", "أخرى"]];

type F = { item: string; type: string; employeeId: string; serial: string; cost: string; warranty: string };
export function validateAsset(f: F): Record<string, string> {
  const e: Record<string, string> = {};
  if (!f.item.trim()) e.item = "required";
  if (!f.type.trim()) e.type = "required";
  const c = f.cost.trim() === "" ? 0 : Number(f.cost);
  if (!Number.isFinite(c) || c < 0) e.cost = "must be ≥ 0";
  if (f.warranty && !/^\d{4}-\d{2}-\d{2}$/.test(f.warranty)) e.warranty = "valid date required";
  return e;
}

function NewAssetModal({ onClose }: { onClose: () => void }) {
  const { t, L, name } = useI18n();
  const toast = useToast();
  const emps = useEmpMap();
  const create = useCreateAsset();
  const [f, setF] = useState<F>({ item: "", type: "Laptop", employeeId: "", serial: "", cost: "", warranty: "" });
  const [touched, setTouched] = useState(false);
  const [srv, setSrv] = useState<Record<string, string>>({});
  const errs = { ...(touched ? validateAsset(f) : {}), ...srv };
  const set = (k: keyof F) => (v: string) => { setF((x) => ({ ...x, [k]: v })); setSrv({}); };
  const submit = async () => {
    setTouched(true);
    if (Object.keys(validateAsset(f)).length) return;
    const input: AssetInput = { item: f.item.trim(), type: f.type, employeeId: f.employeeId ? Number(f.employeeId) : null, serial: f.serial.trim(), cost: f.cost.trim() === "" ? 0 : numOf(f.cost), warranty: f.warranty || "—" };
    try {
      const a = await create.mutateAsync(input);
      toast(L("Asset added · ", "تمت إضافة الأصل · ") + a.tag);
      onClose();
    } catch (e) {
      const fe = (e as { fields?: Record<string, string> }).fields;
      if (fe && Object.keys(fe).length) setSrv(fe); else toast(errMsg(e), "bad");
    }
  };
  return (
    <Modal title={L("Add asset", "إضافة أصل")} onClose={onClose} testId="asset-modal"
      foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" onClick={submit} loading={create.isPending} data-testid="asset-submit">{t("Save")}</Button></>}>
      <div className="stack">
        <TextField label={L("Item", "الأصل")} value={f.item} onChange={set("item")} err={errs.item} req testId="asset-item" placeholder="Dell Latitude 5440" />
        <div className="form2">
          <SelectField label={t("Type")} value={f.type} onChange={set("type")} options={ASSET_TYPES.map(([k, ar]): [string, string] => [k, L(k, ar)])} err={errs.type} req testId="asset-type" />
          <SelectField label={L("Assigned to", "مسلم إلى")} value={f.employeeId} onChange={set("employeeId")} options={[["", L("In stock (unassigned)", "في المخزون")], ...emps.list.map((e): [number, string] => [e.id, name(e) + " · " + e.code])]} err={errs.employeeId} testId="asset-employee" />
          <TextField label={L("Serial / plate", "الرقم التسلسلي")} value={f.serial} onChange={set("serial")} dir="ltr" testId="asset-serial" />
          <Field label={L("Cost", "التكلفة")} err={errs.cost} htmlFor="as-cost"><Affix pre={curL(L)}><Input id="as-cost" className="num" type="number" min={0} value={f.cost} onChange={(e) => set("cost")(e.target.value)} bad={!!errs.cost} data-testid="asset-cost" /></Affix></Field>
          <TextField label={L("Warranty until", "الضمان حتى")} type="date" value={f.warranty} onChange={set("warranty")} err={errs.warranty} testId="asset-warranty" />
        </div>
      </div>
    </Modal>
  );
}

export default function Assets() {
  const { L, t, name, lang } = useI18n();
  const today = useToday();
  const q = useAssets();
  const emps = useEmpMap();
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [open, setOpen] = useState(false);
  const list = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (q.data ?? []).filter((a) => (!type || a.type === type) && (!s || [a.item, a.tag, a.serial, a.employeeId ? emps.map.get(a.employeeId)?.nameEn ?? "" : ""].some((x) => x.toLowerCase().includes(s))));
  }, [q.data, search, type, emps.map]);
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton />;
  const all = q.data;
  const typeAr = (k: string) => ASSET_TYPES.find((x) => x[0] === k)?.[1] ?? k;

  return (
    <>
      <PageHeader title={L("Assets", "العهد والأصول")} sub={L("Laptops, phones, vehicles, SIMs and access cards — who has what, and what must come back at exit.", "الحواسيب والجوالات والسيارات والشرائح وبطاقات الدخول — من يحمل ماذا وما يجب إعادته عند الخروج.")}
        actions={<Button variant="pri" icon="plus" onClick={() => setOpen(true)} data-testid="btn-add-asset">{L("Add asset", "إضافة أصل")}</Button>} />
      <div className="g g-4 mb">
        <Kpi testId="kpi-assets" label={L("Assets", "الأصول")} value={String(all.length)} icon="layers" />
        <Kpi testId="kpi-assigned" label={L("Assigned", "مسلّمة")} value={String(all.filter((a) => a.employeeId != null).length)} icon="user" tone="info" />
        <Kpi testId="kpi-stock" label={L("In stock", "في المخزون")} value={String(all.filter((a) => a.employeeId == null).length)} icon="inbox" />
        <Kpi testId="kpi-value" label={L("Book value", "القيمة")} value={<Money value={all.reduce((s, a) => s + a.cost, 0)} dec={0} plain />} unit={curL(L)} icon="banknote" tone="gold" />
      </div>
      <section className="card">
        <div className="toolbar">
          <SearchBox value={search} onChange={setSearch} placeholder={t("Search") + "…"} testId="asset-search" />
          <Select aria-label={t("Type")} value={type} onChange={(e) => setType(e.target.value)} options={[["", L("All types", "كل الأنواع")], ...ASSET_TYPES.map(([k, ar]): [string, string] => [k, L(k, ar)])]} data-testid="asset-type-filter" />
        </div>
        {list.length === 0 ? <EmptyState title={L("No assets found", "لا توجد أصول")} /> : (
          <div className="tw">
            <table className="tbl cards" data-testid="assets-table">
              <thead><tr><th>{L("Asset", "الأصل")}</th><th>{L("Assigned to", "مسلم إلى")}</th><th className="hide-md">{L("Serial / plate", "الرقم التسلسلي")}</th><th className="end hide-md">{L("Cost", "التكلفة")}</th><th className="hide-md">{L("Warranty", "الضمان")}</th><th>{t("Status")}</th></tr></thead>
              <tbody>
                {list.map((a) => {
                  const e = a.employeeId != null ? emps.map.get(a.employeeId) : undefined;
                  const w = /^\d{4}-\d{2}-\d{2}$/.test(a.warranty);
                  return (
                    <tr key={a.tag} data-testid={"asset-row-" + a.tag}>
                      <td className="k1"><b>{a.item}</b><div className="xs muted num">{a.tag} · {L(a.type, typeAr(a.type))}</div></td>
                      <td className="k3" data-l={L("Assigned to", "مسلم إلى")}>{e ? name(e) : "—"}</td>
                      <td className="hide-md num sm">{a.serial || "—"}</td>
                      <td className="end hide-md">{a.cost ? <Money value={a.cost} dec={0} /> : "—"}</td>
                      <td className="hide-md">{w ? (daysTo(today, a.warranty) <= 90 ? <ExpiryChip date={a.warranty} today={today} /> : gDate(a.warranty, lang)) : "—"}</td>
                      <td className="k2"><StatusChip status={a.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {open && <NewAssetModal onClose={() => setOpen(false)} />}
    </>
  );
}
